import { bffCrmPersonClaims } from '@fireflyhealth/api'
import type { BffCrmPersonClaimsRetrieveResponse } from '@fireflyhealth/api'

import { useInfiniteQuery } from '~/shared/hooks/query'
// Matches the app's claims list page size (server default is also 50).
const PAGE_SIZE = 50

/**
 * Cache key for a member's claims history. Module-private for now (the tab is read-only, so nothing
 * invalidates it yet); export it when a mutation needs to invalidate this query.
 */
const personClaimsQueryKey = (personId: number) =>
  bffCrmPersonClaims.retrieveQueryKey({ person_id: personId })

/**
 * Fetches a member's claims from the CRM BFF (`GET /bff/crm/person/<person_id>/claims/`) as an
 * infinite query paginated on `nextPageToken`, mirroring the app's `useClaims`. Each page is the
 * PHI-gated, server-built response (`claims`, `nextPageToken`); the client just renders it. Disabled
 * until a `personId` is available — pass `undefined` to keep the tab from issuing a request (e.g.
 * while its feature switch is off).
 */
export const useGetPersonClaims = (variables: { personId: number | undefined }) => {
  const { personId } = variables
  return useInfiniteQuery(
    personId != null ? personClaimsQueryKey(personId) : ['retrieveBffCrmPersonClaims', {}],
    async ({ pageParam }: { pageParam: number }): Promise<BffCrmPersonClaimsRetrieveResponse> =>
      (
        await bffCrmPersonClaims.retrieve(
          { person_id: Number(personId) },
          { params: { pageToken: pageParam, pageSize: PAGE_SIZE } },
        )
      ).data,
    {
      enabled: personId != null,
      initialPageParam: 0,
      // The server returns "" when there are no more pages; map a present token back to the numeric
      // page cursor the endpoint expects, or undefined to stop paginating.
      getNextPageParam: lastPage =>
        lastPage.nextPageToken ? Number(lastPage.nextPageToken) : undefined,
    },
    { error: 'Failed to fetch claims' },
  )
}
